import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import capitalize from 'lodash/capitalize';
import Text from '@components/Text/Text';
import WelcomeDropDown from '@components/WelcomeDropdown/WelcomeDropDown';
import { setOfferTypeSelected, setSelectedLoanNumber } from '@store/actions/widgets.actions';
import { useDetectOutsideClick } from '@hooks/useDetectOutsideClick';
import { WelcomeBarProps } from '../types';
import { Identifier } from '@types';
import { lastDigits } from '@utils/lastDigits';
import { CodingConstants, UIConstants, WelcomeBarData } from '@constants';
import {
    AccountText,
    DropDownContainer,
    DropDownHeader,
    DropDownLable,
    DropDownWrapper,
    WelcomeBarMain,
    WelcomeBarContent,
    NavigationTitle,
    NavigationBreadCrumb,
    NavigationBreadCrumbContainer
} from './WelcomeBar.styled';
import { getFullAppFeatureFlags, getLipV2FeatureFlag } from '@store/reducers/config.reducer';
import { get } from 'lodash';
import { selectOfferTypeSelected } from '@store/selectors/widgets.selectors';
import { selectHasFullAppFlow } from '@store/selectors/fullApp.selector';
import { setShowFullAppExitModal } from '@store/actions/fullApp.action';

export const WelcomeBar: React.FC<WelcomeBarProps> = ({ name, loanOptions, getOption, defaultOption, content, updateLoanType }) => {
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);
    const [isActive, setIsActive] = useDetectOutsideClick(dropdownRef, false);
    const [selectedOption, setSelectedOption] = useState(loanOptions[0]);
    const [isWelcomeGreeting, setIsWelcomeGreeting] = useState(true);
    const [navigation, setNavigation] = useState([]);
    const [isLoanDropDownNeeded, setIsLoanDropDownNeeded] = useState(true);
    const [navigationTitle, setNavigationTitle] = useState('');
    const flags = useSelector(getFullAppFeatureFlags);
    const productCode = useSelector(selectOfferTypeSelected);
    const hasFullAppFlow: boolean = useSelector(selectHasFullAppFlow);
    const lipV2flags = useSelector(getLipV2FeatureFlag);

    const onClickHome = () => {
        if (hasFullAppFlow) {
            dispatch(setShowFullAppExitModal(true));
        } else {
            dispatch(setOfferTypeSelected(undefined));
            const options = loanOptions?.filter(option => option.type == CodingConstants.servicingLoanType);
            if (options && options.length > 0) getOption(options[0]);
        }
    };
    const getSelectedOption = (options: Identifier) => {
        setIsActive(false);
        setSelectedOption(options);
        getOption(options);
        renderWelComeUserGreeting(options);
        dispatch(setSelectedLoanNumber(options));
    };
    const firstName = name.split(' ')[0];
    useEffect(() => {
        setSelectedOption(defaultOption);
        renderWelComeUserGreeting(defaultOption);
        dispatch(setSelectedLoanNumber(defaultOption));
        switch (content) {
            case 'escrow':
                setIsWelcomeGreeting(false);
                setIsLoanDropDownNeeded(WelcomeBarData.escrow.isLoanDropDownNeeded);
                setNavigation(WelcomeBarData.escrow);
                break;
            case 'OriginationLoanNumber':
                if (lipV2flags) {
                    setIsWelcomeGreeting(false);
                    setNavigation(WelcomeBarData.inProgressLoanTypeV2);
                } else {
                    setNavigation(WelcomeBarData.inProgressLoanType);
                }
                break;
            case 'authCashOut':
                setIsWelcomeGreeting(false);
                setIsLoanDropDownNeeded(WelcomeBarData.cashOut.isLoanDropDownNeeded);
                setNavigation(WelcomeBarData.cashOut);
                break;
            case 'authRefi':
                setIsWelcomeGreeting(false);
                if (get(flags, [productCode], false)) {
                    setIsLoanDropDownNeeded(WelcomeBarData.authRefiFullApp.isLoanDropDownNeeded);
                    setNavigation(WelcomeBarData.authRefiFullApp);
                } else {
                    setNavigation(WelcomeBarData.authRefi);
                    setIsLoanDropDownNeeded(WelcomeBarData.authRefi.isLoanDropDownNeeded);
                }
                break;
            case CodingConstants.naturalDisaster:
                setIsWelcomeGreeting(false);
                setIsLoanDropDownNeeded(WelcomeBarData.naturalDisaster.isLoanDropDownNeeded);
                setNavigation(WelcomeBarData.naturalDisaster);
                break;
            case CodingConstants.naturalDisasterAssistance:
                setIsWelcomeGreeting(false);
                setIsLoanDropDownNeeded(WelcomeBarData.naturalDisasterAssistance.isLoanDropDownNeeded);
                setNavigation(WelcomeBarData.naturalDisasterAssistance);
                break;
            case CodingConstants.purchaseWidgetId:
                setIsWelcomeGreeting(false);
                setIsLoanDropDownNeeded(WelcomeBarData.authPurchase.isLoanDropDownNeeded);
                setNavigation(WelcomeBarData.authPurchase);
                break;
            default:
                setIsLoanDropDownNeeded(true);
        }
    }, [defaultOption, content]);

    useEffect(() => {
        const activeNavigation = navigation?.filter(function (navigation: { isActive: boolean }) {
            return navigation.isActive === true; // Filter out the appropriate one
        });
        if (activeNavigation.length > 0) {
            setNavigationTitle(activeNavigation[0].description);
        }
    }, [navigation]);
    const toggleMenu = () => {
        if (loanOptions?.length !== 1) {
            setIsActive(prev => !prev);
        }
    };
    const checkLoansByType = (currLoan: Identifier) => {
        const loanArr = [...new Set(loanOptions.map(loan => loan.type))];
        return loanArr.length === 1 || loanOptions[0].identifier === currLoan?.identifier ? true : currLoan?.type === 'ServicingLoanNumber';
    };
    const renderWelComeUserGreeting = (currLoan: Identifier) => {
        const isShown = loanOptions?.length === 1 ? true : checkLoansByType(currLoan);
        setIsWelcomeGreeting(isShown);
    };
    //  document.getElementById('contentContainer')?.addEventListener('click', () => setIsActive(false)); - TODO this line is causing user not to toggle between accounts hence commenting it out.
    return (
        <WelcomeBarMain id="welcomeBar">
            <WelcomeBarContent>
                {isWelcomeGreeting ? (
                    <>
                        <div>
                            <Text textVariant="h3">Hi {capitalize(firstName)}!</Text>
                            <NavigationTitle>{UIConstants.welcomeText}</NavigationTitle>
                        </div>
                    </>
                ) : (
                    Array.isArray(navigation) &&
                    navigation.length > 0 && (
                        <div data-testid="navigationHeader">
                            <NavigationBreadCrumbContainer>
                                <NavigationBreadCrumb data-testid="navigationBreadCrumb" onClick={onClickHome}>
                                    {'Home'}
                                </NavigationBreadCrumb>
                                {Array.isArray(navigation) &&
                                    navigation.map(navigation => (
                                        <>
                                            <NavigationBreadCrumb onClick={onClickHome}>{'> '}</NavigationBreadCrumb>
                                            <NavigationBreadCrumb
                                                key={navigation.title}
                                                data-testid="navigationBreadCrumbChilds"
                                                onClick={() => navigation.isClickable && updateLoanType(navigation.loanType)}
                                            >
                                                {navigation.title}
                                            </NavigationBreadCrumb>
                                        </>
                                    ))}
                            </NavigationBreadCrumbContainer>
                            {<NavigationTitle data-testid="navigationTitle">{navigationTitle}</NavigationTitle>}
                        </div>
                    )
                )}
                {isLoanDropDownNeeded && (
                    <DropDownContainer ref={dropdownRef}>
                        {loanOptions?.length !== 1 && <AccountText>{UIConstants.accountSelection}</AccountText>}
                        <DropDownWrapper onClick={toggleMenu}>
                            <DropDownLable>
                                {selectedOption?.propertyAddress
                                    ? `${selectedOption?.propertyAddress?.address1} (...${lastDigits(selectedOption?.identifier, 4)})`
                                    : `(...${lastDigits(selectedOption?.identifier, 4)})`}
                            </DropDownLable>
                            <DropDownHeader data-testid="dropdownHeader" isActive={isActive}>
                                {loanOptions.length > 1 && (
                                    <img
                                        data-testid="dropdownimg"
                                        src="https://dam.freedommortgage.com/dx/api/dam/v1/collections/563a2e94-abdb-4eda-a407-49cc1380b6f4/items/54eecfb8-fa4b-4752-8304-c363763f3e53/renditions/0c46bbda-df21-4f20-9893-d56c433a33b5?binary=true"
                                    />
                                )}
                            </DropDownHeader>
                        </DropDownWrapper>
                        {isActive && (
                            <WelcomeDropDown
                                data-testid="welcomedropDown"
                                options={loanOptions}
                                //ref={dropdownRef}
                                getSelectedOption={getSelectedOption}
                                selectedOption={selectedOption}
                            />
                        )}
                    </DropDownContainer>
                )}
            </WelcomeBarContent>
        </WelcomeBarMain>
    );
};

export default React.memo(WelcomeBar);
